import { Injectable, Signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SessionService } from '../session/session.service';
import { PermissionService, PermissionArea } from '../auth/permission.service';
import { AmieAction, ActionAvailabilityContext, isActionAvailable } from './action.model';
import { buildCallViewerLink } from '../../shared/utils/call-viewer-link.util';

/**
 * Central curated-core action catalog for the unified search + launchpad (spec §5.7).
 * `availableActions` re-derives from session + permission signals, so context-gated
 * actions appear/disappear automatically. run() handlers read the session live.
 */
@Injectable({ providedIn: 'root' })
export class ActionRegistryService {
  private readonly router = inject(Router);
  private readonly sessionService = inject(SessionService);
  private readonly permissionService = inject(PermissionService);

  private readonly context: Signal<ActionAvailabilityContext> = computed(() => ({
    hasCustomer: this.sessionService.customerId() !== null,
    hasUserAccount: this.sessionService.selectedUserAccount() !== null,
    hasPhoneService: this.sessionService.selectedPhoneService() !== null,
    can: (permission: PermissionArea) => this.permissionService.canRead(permission),
  }));

  readonly availableActions: Signal<AmieAction[]> = computed(() => {
    const ctx = this.context();
    return this.allActions().filter((a) => isActionAvailable(a, ctx));
  });

  private allActions(): AmieAction[] {
    return [...this.globalActions(), ...this.customerActions(), ...this.uaActions(), ...this.psActions()];
  }

  private globalActions(): AmieAction[] {
    return [
      {
        id: 'nav-home', title: 'Go to Home', subtitle: 'Search / landing page',
        icon: 'lucideHouse', category: 'navigation', requires: 'none',
        keywords: ['home', 'search', 'landing'],
        run: () => { void this.router.navigate(['/']); },
      },
      {
        id: 'nav-calls', title: 'Go to Call Viewer', subtitle: 'Call history',
        icon: 'lucidePhone', category: 'navigation', requires: 'none',
        keywords: ['calls', 'call viewer', 'history'],
        run: () => { void this.router.navigate(['/calls']); },
      },
      {
        id: 'nav-brands', title: 'Go to Brand Manager', subtitle: 'Manage brands',
        icon: 'lucideBuilding2', category: 'navigation', requires: 'none', permission: 'brandManager',
        keywords: ['brands', 'brand manager', 'admin'],
        run: () => { void this.router.navigate(['/admin']); },
      },
    ];
  }

  private customerActions(): AmieAction[] {
    const nav = (id: string, seg: string, title: string, subtitle: string, icon: string): AmieAction => ({
      id, title, subtitle, icon, category: 'navigation', requires: 'customer',
      run: () => { void this.router.navigate([`/${this.sessionService.customerId()}/${seg}`]); },
    });
    return [
      nav('cust-information', 'information', 'Go to Information', 'Customer details & contact', 'lucideInfo'),
      nav('cust-contracts', 'contracts', 'Go to Contracts', 'Contracts & phone services', 'lucideFileText'),
      nav('cust-invoice', 'invoice', 'Go to Invoice & Billing', 'Billing & invoice history', 'lucideReceipt'),
      nav('cust-tariffcheck', 'tariffcheck', 'Go to Tariff Check', 'Compare tariff options', 'lucideCalculator'),
      nav('cust-smt', 'smt', 'Go to Manage Services', 'Bookable services', 'lucideLayoutGrid'),
      {
        id: 'cust-edit', title: 'Edit Customer', subtitle: 'Edit customer information',
        icon: 'lucidePencil', category: 'action', requires: 'customer',
        run: () => { void this.router.navigate([`/${this.sessionService.customerId()}/information`], { queryParams: { action: 'edit' } }); },
      },
      {
        id: 'cust-print', title: 'Print Address Label', subtitle: 'Open the print dialog',
        icon: 'lucidePrinter', category: 'action', requires: 'customer',
        run: () => { void this.router.navigate([`/${this.sessionService.customerId()}/information`], { queryParams: { action: 'printLabel' } }); },
      },
      {
        id: 'cust-callviewer', title: 'Open Call Viewer for this customer', subtitle: 'Calls filtered to this customer',
        icon: 'lucidePhone', category: 'action', requires: 'customer',
        run: () => {
          const link = buildCallViewerLink('customer', { customerId: this.sessionService.customerId()! });
          void this.router.navigate(link.commands, { queryParams: link.queryParams });
        },
      },
    ];
  }

  private uaActions(): AmieAction[] {
    const cid = () => this.sessionService.customerId();
    const uid = () => this.sessionService.selectedUserAccount()!.id;
    const nav = (id: string, seg: string, title: string, subtitle: string, icon: string): AmieAction => ({
      id, title, subtitle, icon, category: 'navigation', requires: 'ua',
      run: () => { void this.router.navigate([`/${cid()}/ua/${uid()}/${seg}`]); },
    });
    return [
      nav('ua-account', 'account-details', 'Go to User Account', 'Account details', 'lucideUserRound'),
      nav('ua-communication', 'communication', 'Go to Communication', 'Emails, fax, notifications', 'lucideMail'),
      {
        id: 'ua-callviewer', title: 'Open Call Viewer for this account', subtitle: 'Calls filtered to this user account',
        icon: 'lucidePhone', category: 'action', requires: 'ua',
        run: () => {
          const link = buildCallViewerLink('ua', { customerId: cid()!, userAccountId: uid() });
          void this.router.navigate(link.commands, { queryParams: link.queryParams });
        },
      },
    ];
  }

  private psActions(): AmieAction[] {
    const cid = () => this.sessionService.customerId();
    const pid = () => this.sessionService.selectedPhoneService()!.id;
    const nav = (id: string, seg: string, title: string, subtitle: string, icon: string): AmieAction => ({
      id, title, subtitle, icon, category: 'navigation', requires: 'ps',
      run: () => { void this.router.navigate([`/${cid()}/ps/${pid()}/${seg}`]); },
    });
    return [
      nav('ps-settings', 'settings', 'Go to Phone Service', 'Settings & skills', 'lucidePhone'),
      nav('ps-reachabilities', 'reachabilities', 'Go to Reachabilities', 'Reachability rules', 'lucideClock'),
      nav('ps-numbers', 'numbers', 'Go to CC Numbers', 'Numbers, AM, waiting queue', 'lucideHash'),
      nav('ps-notifications', 'notifications', 'Go to Notifications & Forwardings', 'Notifications & forwardings', 'lucideBell'),
      nav('ps-contacts', 'contacts', 'Go to Contacts', 'Contacts & filter categories', 'lucideContact'),
      {
        id: 'ps-callviewer', title: 'Open Call Viewer for this phone service', subtitle: 'Calls filtered to this phone service',
        icon: 'lucidePhone', category: 'action', requires: 'ps',
        run: () => {
          const link = buildCallViewerLink('ps', { customerId: cid()!, phoneServiceId: pid() });
          void this.router.navigate(link.commands, { queryParams: link.queryParams });
        },
      },
    ];
  }
}
